/**
 * Aside column of the item editor: publishing (status, series, date, hero),
 * web address & order (slug, episode, sort key, topic) and the read-only
 * legacy data (collapsed).
 */
import React, { useId } from 'react';
import { MDBCol, MDBIcon, MDBInput, MDBInputGroup, MDBRow, MDBSelect, MDBSpinner, MDBSwitch } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import SlugField from '../../../components/toolbox/SlugField';
import { pickSourceText } from '../../../utils/slugify';
import { formatLegacyData, fromDatetimeLocal, toDatetimeLocal } from '../../../utils/mediathek';
import { SidePanel, StatusToggle } from '../MediathekEditorLayout';
import { selectValue } from '../MediathekShared';

/**
 * @param {{item: object, itemId?: string, setField: Function, seriesOptions: Array<{text: string, value: string}>, isLoadingSeries: boolean, currentSeries: object|null, isSaving: boolean}} props
 */
export function ItemPublishingPanel({ item, setField, seriesOptions, isLoadingSeries, isSaving }) {
  const { t } = useTranslation();
  const uid = useId();
  const dateId = `${uid}-published-at`;
  return (
    <SidePanel id="publishing" title={t('Publishing')}>
      <StatusToggle value={item.status} onChange={(value) => setField('status', value)} disabled={isSaving} />

      <div className="mt-4">
        {isLoadingSeries ? (
          <div className="d-flex align-items-center gap-2 me-muted small" role="status">
            <MDBSpinner size="sm" role="presentation" />
            <span>{t('Series')}</span>
          </div>
        ) : (
          <MDBSelect
            label={`${t('Series')} *`}
            search
            searchLabel={t('Search')}
            data={seriesOptions}
            value={item.mediaSeriesId || ''}
            onChange={(entry) => {
              const value = selectValue(entry);
              if (value !== undefined && value !== (item.mediaSeriesId || '')) setField('mediaSeriesId', value);
            }}
          />
        )}
      </div>

      <div className="mt-4">
        <label htmlFor={dateId} className="me-help d-block mt-0 mb-1">
          {t('Published at')} ({t('sort order on the website')})
        </label>
        <MDBInputGroup textBefore={<MDBIcon far icon="calendar-alt" aria-hidden="true" />}>
          <input
            id={dateId}
            type="datetime-local"
            className="form-control"
            value={toDatetimeLocal(item.publishedAt)}
            onChange={(e) => setField('publishedAt', fromDatetimeLocal(e.target.value))}
          />
        </MDBInputGroup>
      </div>

      <div className="mt-4">
        <MDBSwitch
          id={`${uid}-hero`}
          label={t('Hero item (highlighted on the website)')}
          checked={Boolean(item.isHero)}
          onChange={(e) => setField('isHero', e.target.checked)}
        />
      </div>
    </SidePanel>
  );
}

/** Slug (unique per series), episode number, sort key, topic. */
export function ItemAddressPanel({ item, itemId, setField, currentSeries, isSaving }) {
  const { t } = useTranslation();
  const uid = useId();
  return (
    <SidePanel id="web-address" title={t('Web address & order')}>
      <SlugField
        entity="mediaItem"
        entityId={itemId}
        value={item.slug}
        history={item.slugHistory}
        sourceText={pickSourceText(item.title)}
        scopeValue={item.mediaSeriesId || undefined}
        pathPrefixOverride={currentSeries?.slug ? `/mediathek/${currentSeries.slug}/` : undefined}
        helpText={t('Generated from the German title on save when left empty')}
        disabled={isSaving}
        onChange={(slug) => setField('slug', slug)}
      />
      <MDBRow className="g-3">
        <MDBCol size="6">
          <MDBInput
            id={`${uid}-episode`}
            label={t('Episode number')}
            type="number"
            step="1"
            inputMode="numeric"
            value={item.episodeNumber}
            onChange={(e) => setField('episodeNumber', e.target.value)}
          />
        </MDBCol>
        <MDBCol size="6">
          <MDBInput
            id={`${uid}-sort`}
            label={t('Sort key')}
            type="number"
            step="1"
            inputMode="numeric"
            value={item.sortKey}
            aria-describedby={`${uid}-sort-help`}
            onChange={(e) => setField('sortKey', e.target.value)}
          />
        </MDBCol>
      </MDBRow>
      <div className="me-help text-end" id={`${uid}-sort-help`}>{t('Empty = shown first')}</div>
      <div className="mt-3">
        <MDBInput id={`${uid}-topic`} label={t('Topic')} value={item.topic} onChange={(e) => setField('topic', e.target.value)} />
      </div>
    </SidePanel>
  );
}

/** Read-only legacy data (collapsed); renders nothing without legacy data. */
export function ItemLegacyPanel({ item }) {
  const { t } = useTranslation();
  const legacyText = formatLegacyData(item.legacyData);
  if (!legacyText && !item.legacyKey) return null;
  return (
    <SidePanel>
      <details className="me-legacy">
        <summary>{t('Legacy data (read only)')}</summary>
        {item.legacyKey && (
          <div className="small mt-2">{t('Legacy key')}: <code>{item.legacyKey}</code></div>
        )}
        {legacyText && (
          <pre className="small border rounded p-2 mt-2 mb-0" style={{ maxHeight: '400px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {legacyText}
          </pre>
        )}
      </details>
    </SidePanel>
  );
}
